import { useQuery, useQueryClient } from "react-query";
import { useCallback, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/services/functions";
import { useStore } from "@/stores/useStore";
import { firestore } from "@/services/firestore";
import { CardStatusMetric } from "@/types/v2/PrecomputedMetrics";

interface ComputeCardStatusMetricsRequest {
  shopId: string;
  dateRangeStart?: string;
  dateRangeEnd?: string;
}

export interface ComputeCardStatusMetricsResponse {
  cacheKey: string;
  metric: CardStatusMetric | null;
  cached: boolean;
  isStale: boolean;
  backgroundRefresh: boolean;
  computationTimeMs: number;
}

/**
 * Enhanced hook for fetching card status metrics with intelligent caching
 * Integrates with cloud functions and provides real-time cache invalidation
 * Follows the same pattern as useOrderCounts for consistency
 * @param params Optional date range parameters
 * @param params.start Start date for the card status query
 * @param params.end End date for the card status query
 */
export function useCardStatusMetrics(params?: { start: Date; end: Date }) {
  const { shop } = useStore();
  const queryClient = useQueryClient();
  const shopId = shop?.id;
  const lastCacheKeyRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const requestConfig = {
    shopId: shopId!,
    dateRangeStart: params?.start.toISOString(),
    dateRangeEnd: params?.end.toISOString(),
  };

  // Generate stable query key including date params
  const queryKey = ["cardStatusMetrics", shopId, requestConfig];

  // Create the callable function outside of query to avoid recreating
  const computeCardStatusMetricsCallable = useCallback(() => {
    return httpsCallable<
      ComputeCardStatusMetricsRequest,
      ComputeCardStatusMetricsResponse
    >(functions, "functionsV2-computeCardStatusMetrics");
  }, []);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shopId) throw new Error("Shop ID not available");

      const callable = computeCardStatusMetricsCallable();
      const result = await callable(requestConfig);

      // Return the enhanced response from the cloud function which includes cache metadata
      return result.data;
    },
    enabled: !!shopId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour - card status changes frequently with card updates
    cacheTime: 1000 * 60 * 60 * 8, // 8 hours - keep in memory for 8 hours
    onError: (error) => {
      console.error("Error fetching card status metrics:", error);
    },
  });

  // Set up real-time cache invalidation listener for card status metrics
  useEffect(() => {
    if (!shopId || !query.data?.cacheKey) {
      return;
    }

    const currentCacheKey = query.data.cacheKey;

    // Only set up a new subscription if cacheKey actually changed
    if (currentCacheKey === lastCacheKeyRef.current) {
      return;
    }

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const metricPath = `shops/${shopId}/cardStatusMetrics/${currentCacheKey}`;
    let lastStaleState: boolean | undefined = undefined;

    unsubscribeRef.current = firestore.subscribeDocument<CardStatusMetric>(
      metricPath,
      (snapshot) => {
        if (snapshot) {
          const metricData = snapshot as CardStatusMetric;
          const currentStaleState = metricData.cache.isStale;

          // Only invalidate if cache became newly stale (transition from false/undefined to true)
          // This prevents loops from age-based staleness that doesn't require invalidation
          if (currentStaleState && lastStaleState === false) {
            // Use setTimeout to avoid immediate refetch during render
            setTimeout(() => {
              queryClient.invalidateQueries(queryKey);
            }, 100);
          }

          lastStaleState = currentStaleState;
        }
      }
    );

    lastCacheKeyRef.current = currentCacheKey;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [shopId, query.data?.cacheKey, queryClient, queryKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    ...query,
    statusBreakdown: query.data?.metric?.statusBreakdown,
    completionTimeStats: query.data?.metric?.completionTimeStats,
    completionTimeDistribution: query.data?.metric?.completionTimeDistribution,
    cached: query.data?.cached || false,
    isStale: query.data?.isStale || false,
    backgroundRefresh: query.data?.backgroundRefresh || false,
    computationTimeMs: query.data?.computationTimeMs || 0,
    cacheKey: query.data?.cacheKey,
  };
}

/**
 * Query factory for consistent query key generation
 * @param shopId
 */
export const cardStatusMetricsQuery = (shopId: string) => ({
  queryKey: ["cardStatusMetrics", shopId],
  queryFn: async () => {
    const computeCardStatusMetricsCallable = httpsCallable<
      ComputeCardStatusMetricsRequest,
      ComputeCardStatusMetricsResponse
    >(functions, "functionsV2-computeCardStatusMetrics");

    const result = await computeCardStatusMetricsCallable({
      shopId,
    });

    return result.data;
  },
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 60, // 1 hour
  cacheTime: 1000 * 60 * 60 * 8, // 8 hours
});
